import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import CustomLayout from "../../../../../Core/Layout/CustomLayout";
import Header from "./Header";
import ColumnHeader from "./ColumnHeader";
import Row from "./Row";
import ScheduleTripModal from "./Modals/ScheduleTripModal";
import { updateUsers } from "../../../../../Redux/Slices/users.slice";
import ScheduleSessionModal from "./Modals/ScheduleSessionModal";
import TimeoffModal from "./Modals/TimeoffModal";
import ReassignTimeoffModal from "./ReassignTimeoffModal";
import { client } from "../../../../../Utils/axiosClient";

const CalendarScreen = () => {
  const {
    scheduleTripModal,
    scheduleSessionModal,
    timeoffModal,
    transferTimeoffModal,
  } = useSelector((state) => state.users);

  const dispatch = useDispatch();

  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const initialLoadDoneRef = useRef(true);

  const [driversData, setDriversData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    startDate: dayjs().startOf("week"),
    endDate: dayjs().endOf("week"),
    hub: [],
  });

  const fetchCalendar = async (force = false) => {
    if (!hasMore && !force) return;

    // setLoading(true);
    dispatch(
      updateUsers({
        key: "loading",
        value: true,
      }),
    );

    try {
      const res = await client.get(
        `${process.env.REACT_APP_API_URL}/drivers/calender`,
        {
          params: {
            startDate: filters.startDate.toISOString(),
            endDate: filters.endDate.toISOString(),
            hubId: filters.hub || undefined,
            search: search || undefined,
            page,
            limit: 10,
          },
        },
      );

      if (page === 1) {
        initialLoadDoneRef.current = true;
      }

      const newDrivers = res.data.drivers || [];

      if (page === 1) {
        setDriversData(newDrivers);
      } else {
        setDriversData((prev) => [...prev, ...newDrivers]);
      }

      setHasMore(res.data.hasMore);
    } catch (err) {
      console.log("calendar error", err);
    } finally {
      dispatch(
        updateUsers({
          key: "loading",
          value: false,
        }),
      );
    }
  };

  const reFetch = async () => {
    initialLoadDoneRef.current = false;

    setPage(1);
    setHasMore(true);
    setDriversData([]);

    await fetchCalendar();
  };

  useEffect(() => {
    initialLoadDoneRef.current = false;

    setPage(1);
    setHasMore(true);
    setDriversData([]);
  }, [filters, search]);

  useEffect(() => {
    fetchCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters, search]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting && hasMore && initialLoadDoneRef.current) {
          setPage((prev) => prev + 1);
        }
      },
      {
        root: scrollRef.current,
        threshold: 0.1,
      },
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore]);

  return (
    <CustomLayout pageName="Users" nestedName="Calendar" hideFooter>
      <div>
        <Header
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setHasMore={setHasMore}
          setPage={setPage}
        />
        <div
          ref={scrollRef}
          className="w-full max-h-[calc(100vh-72px-60px)] overflow-y-scroll relative"
        >
          <ColumnHeader
            startDate={filters.startDate}
            endDate={filters.endDate}
            driversCount={driversData?.length}
          />
          {driversData?.map((driver) => (
            <Row
              key={driver.id}
              driver={driver}
              startDate={filters.startDate}
              endDate={filters.endDate}
            />
          ))}

          <div ref={sentinelRef} style={{ height: 2 }} />
        </div>
      </div>
      <ScheduleTripModal
        visible={scheduleTripModal?.visible}
        onCancel={() =>
          dispatch(
            updateUsers({
              key: "scheduleTripModal",
              value: { visible: false, data: {} },
            }),
          )
        }
      />

      <ScheduleSessionModal
        visible={scheduleSessionModal?.visible}
        onCancel={() =>
          dispatch(
            updateUsers({
              key: "scheduleSessionModal",
              value: { visible: false, data: {} },
            }),
          )
        }
      />

      <TimeoffModal
        visible={timeoffModal?.visible}
        onCancel={() =>
          dispatch(
            updateUsers({
              key: "timeoffModal",
              value: { visible: false, data: {} },
            }),
          )
        }
        onSuccess={async () => {
          await reFetch();
        }}
      />

      <ReassignTimeoffModal
        visible={transferTimeoffModal?.visible}
        onCancel={async () => {
          dispatch(
            updateUsers({
              key: "transferTimeoffModal",
              value: { visible: false, data: {} },
            }),
          );
          await reFetch();
        }}
      />
    </CustomLayout>
  );
};

export default CalendarScreen;
